import React, { FC, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, notification } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useCreateAdminMutation } from 'app/services/edpApiV1/admins';
import { CreateAdminRequest } from 'shared/types/admins.types';
import OrganizationsInput from 'features/reports/form-record/organizationsInput/OrganizationsInput';
import { TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import { useAppSelector } from 'app/store';
import { generatePass } from '../../../shared/helpers/utils';
import ModerationCheckboxes from '../moderationCheckboxes/ModerationCheckboxes';
import styles from './index.module.scss';
import RoleInput from './roleInput/RoleInput';
import OrganizationInput from './organizationInput/OrganizationInput';
import UserInput from './userInput/UserInput';

type TAddEmployeeModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  employeeId?: number;
};

export const AddEmployeeModal: FC<TAddEmployeeModalProps> = ({ isModalOpen, setIsModalOpen }) => {
  const [form] = useForm();
  const [email, setEmail] = useState<string>('');
  const [pass, setPass] = useState<string | null>(null);
  const [createUserQuery, { isLoading: isCreateUserLoading }] = useCreateAdminMutation();
  const [orgId, setOrgId] = useState<string>('');
  const [passGenerateLoading, setPassGenerateLoading] = useState<boolean>(false);
  const currentOrganization = useAppSelector(state => state.entities.viewer.me.organization);

  useEffect(() => {
    form.setFieldValue('employeeId', undefined);
  }, [orgId]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setPass(null);
  };

  useEffect(() => {
    if (isModalOpen) {
      form.setFieldValue('organizationIds', [currentOrganization.id]);
    }
  }, [isModalOpen]);

  const onFinish = async (values: CreateAdminRequest) => {
    const realValues: CreateAdminRequest = {
      employeeId: values.employeeId,
      password: values.password,
      email: values.email,
      role: values.role,
      faqModerator: values.faqModerator,
      newsModerator: values.newsModerator,
      pollModerator: values.pollModerator,
      timekeeper: values.timekeeper,
      supervisor: values.supervisor,
      acting: values.acting,
      deputy: values.deputy,
      organizationIds: values.organizationIds,
    };
    await createUserQuery(realValues)
      .then(data => {
        if ((data as any).data.message.includes('Сотрудник не создан')) {
          notification.error({
            message: (data as any).data.message,
            placement: 'topRight',
          });
        } else {
          notification.success({
            message: 'Администратор создан успешно',
            placement: 'topRight',
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Не удалось создать администратора',
          placement: 'topRight',
        });
      });
    Object.keys(form.getFieldsValue()).forEach(key => {
      form.setFieldValue(key, undefined);
    });
    setPass(null);
    setIsModalOpen(false);
  };

  const newPass = async (event: React.MouseEvent) => {
    event.preventDefault();
    setPassGenerateLoading(true);
    const password = await generatePass(16, { login: email });
    setPassGenerateLoading(false);
    form.setFieldValue('password', password);
    setPass(password);
  };

  const onPickAll = (organizations: TOrganizationResponse[]) => {
    const organizationsIds: string[] = organizations
      .filter(({ id }) => id != null)
      .map(elem => {
        return elem.id;
      });
    form.setFieldValue('organizationIds', organizationsIds);
  };

  return (
    <Modal
      title="Добавление нового администратора"
      footer={false}
      onCancel={handleCancel}
      open={isModalOpen}
      bodyStyle={{ padding: '40px' }}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        name="basic"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 700 }}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <OrganizationInput organizationChanged={setOrgId} isSearchField={false} />
        <UserInput
          label="Сотрудник"
          rules={[{ required: true, message: 'Выберите пользователя!' }]}
          className="form-item-add-user"
          organizationId={orgId}
        />
        <RoleInput />
        <OrganizationsInput
          onPickAll={onPickAll}
          label="Организации администратора"
          placeholder="Выберите подконтрольные администратору организации"
          rules={[{ required: true, message: 'Введите организации!' }]}
        />

        <Form.Item
          label="Email"
          name="email"
          style={{ marginBottom: 10 }}
          rules={[
            { required: true, message: 'Введите email!' },
            { type: 'email', message: 'email не валидный' },
          ]}
        >
          <Input onChange={e => setEmail(e.target.value)} />
        </Form.Item>
        <ModerationCheckboxes />
        <Form.Item label="Пароль" name="password" rules={[{ required: true, message: 'Сгенерируйте пароль!' }]}>
          <Input disabled={true} value={pass || ''} />
          <p className={styles.warning}>Скопируйте этот пароль в надежное место</p>
          <div className={styles['right-button']}>
            <Button
              type="primary"
              htmlType="button"
              onClick={(e: React.MouseEvent) => newPass(e)}
              disabled={passGenerateLoading}
            >
              Сгенерировать
            </Button>
          </div>
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit" loading={isCreateUserLoading}>
            Добавить администратора
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
